<template>
    <!-- a click in the menu marks the next navigation as a menu one (header breadcrumb, helpers/crumbTrail) -->
    <div class="w-full" @click.capture="markMenuNav">
        <!-- Sidebar - Added transition classes here -->
        <div class="relative text-white transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]"
            :class="isCollapsed ? 'w-[72px]' : 'laptop:w-[300px] largeDesktop:w-80 mobile:w-full'">
            <!-- Toggle Button - Added transition classes -->
            <!-- Shown once the side menu API has answered (nothing to collapse before that). Figma: 50px white circle;
                 expanded = red lines + ✕ (nav-hide-icon), collapsed = dark lines + ▶ (nav-show-icon). The svg has a 10px
                 shadow margin, so 70px of image = the 50px circle. No rotation: each icon is already drawn the right way. -->
            <div v-if="menuDataLoaded && menuItems.length" class="absolute -top-20 left-0 flex justify-start px-[1px]">
                <button type="button"
                    class="mobile:hidden laptop:block largeDesktop:block p-0 border-0 bg-transparent focus:outline-none"
                    :aria-label="isCollapsed ? 'Expand menu' : 'Collapse menu'" @click="toggleSidebar">
                    <img :src="isCollapsed ? showIcon : hideIcon" alt=""
                        class="w-[70px] h-[70px] max-w-none transition-opacity duration-300">
                </button>
            </div>

            <!-- Side menu API not answered yet: shimmer placeholders shaped like the menu cards (icon circle + label) -->
            <ul v-if="!menuDataLoaded" class="flex flex-col gap-2 p-0 m-0" aria-busy="true" aria-label="Loading menu">
                <li v-for="n in 5" :key="n" class="flex items-center gap-2"
                    :class="isCollapsed ? 'justify-center py-[3px]' : 'p-2 rounded-2xl bg-white'">
                    <span class="fxShimmer w-[50px] h-[50px] rounded-full flex-shrink-0" />
                    <span v-if="!isCollapsed" class="fxShimmer h-[14px] rounded-full"
                        :style="{ width: (110 - n * 9) + 'px' }" />
                </li>
            </ul>
            <!-- Menu Items - Added transition classes -->
            <nav v-else
                :class="isCollapsed ? 'bg-white rounded-full px-[7px] py-[3px]' : 'bg-transparent pb-3 pt-[16px] laptop:pt-0'"
                class="transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]">
                <ul class="flex flex-col gap-2 p-0 m-0">
                    <li v-for="item in menuItems" :key="item.name"
                        class="menuList relative group mobile:border-[1px] mobile:rounded-full tablet:rounded-2xl mobile:py-0 tablet:py-1 tablet:transition-all tablet:duration-500 tablet:ease-[cubic-bezier(0.4,0,0.2,1)]"
                        :class="getMenuItemClasses(item)" :style="getMenuItemStyles(item)">
                        <!-- Main link - Added transition classes -->
                        <div v-if="item.submenu"
                            :class="isCollapsed ? '!border-0 !bg-transparent !p-0' : 'mobile:p-1 tablet:p-2 flex-row '"
                            class="transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]">
                            <div class="flex items-center cursor-pointer transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]"
                                :class="isCollapsed ? '!gap-0 w-auto' : 'gap-2 w-full'" @click="handleMenuClick(item)">
                                <!-- Icon container -->
                                <div
                                    class="mobile:min-w-[40px] mobile:min-h-[40px] tablet:min-w-[58px] tablet:min-h-[58px] largeDesktop:min-w-[58px] largeDesktop:min-h-[58px] transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] overflow-hidden relative">
                                    <img :src="require(`@/assets/images/${item.icon}`)" :alt="item.name"
                                        class="mobile:w-[40px] mobile:h-[40px] tablet:min-w-[58px] tablet:min-h-[58px] largeDesktop:min-w-[58px] largeDesktop:min-h-[58px] transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]">
                                </div>
                                <!-- Label: hidden at once on collapse; on expand it fades in once the sidebar has widened, on one line -->
                                <div class="flex items-center justify-between min-w-0 overflow-hidden whitespace-nowrap transition-opacity ease-out"
                                    :class="isCollapsed ? 'opacity-0 w-0 duration-100' : 'opacity-100 flex-1 duration-300 delay-200'">
                                    <span class="text-[16px] font-roboto font-medium text-black-100 truncate">
                                        {{ item.name }}
                                        <span v-if="item.comingSoon"
                                            class="text-xs  ml-1 font-roboto text-[#999999]">(Coming
                                            Soon)</span>
                                    </span>
                                    <div class="flex items-center gap-3">
                                        <div v-if="!isCollapsed && item.submenu"
                                            class="transition-all duration-100 ease-[cubic-bezier(0,0,0,0.5)] flex items-center justify-center w-[20px] tablet:w-[22px] h-[20px] tablet:h-[22px] transform mr-[10px]"
                                            :class="{ 'rotate-0': item.submenu && (!isCollapsed && activeMenu === item.name), 'rotate-180': !(item.submenu && (!isCollapsed && activeMenu === item.name)) }">
                                            <!-- <i
                                                class="fa fa-angle-up !text-[30px] !font-extralight text-[#666666] transition-all duration-500 ease-[cubic-bezier(0,0,0,0.5)]" /> -->
                                            <img src="../assets/images/arrow-down.svg" alt="arrow"
                                                class="transition-all duration-500 ease-[cubic-bezier(0,0,0,0.5)] w-full object-contain">
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- leaf item: real <a href> (open in new tab / Ctrl/Cmd/middle click); no active-class so the
                             global a.router-link-active colour/border doesn't apply -->
                        <component :is="linkTo(item.route) ? 'router-link' : 'div'" v-else
                            :to="linkTo(item.route) || undefined" active-class="" exact-active-class="" :class="[
                                isCollapsed ? '!border-0 !bg-transparent !p-0' : 'mobile:p-1 tablet:p-2 flex-row',
                                isRouteActive(item) ? 'your-active-extra-class' : ''
                            ]" class="block no-underline text-inherit transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]"
                            @click="handleMenuClick(item, $event)">
                            <div class="flex items-center cursor-pointer transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]"
                                :class="isCollapsed ? '!gap-0 w-auto' : 'gap-2 w-full'">
                                <!-- Icon container -->
                                <div
                                    class="mobile:min-w-[40px] mobile:min-h-[40px] tablet:min-w-[58px] tablet:min-h-[58px] largeDesktop:min-w-[58px] largeDesktop:min-h-[58px] transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] overflow-hidden relative">
                                    <img :src="require(`@/assets/images/${item.icon}`)" :alt="item.name"
                                        class="mobile:w-[40px] mobile:h-[40px] tablet:min-w-[58px] tablet:min-h-[58px] largeDesktop:min-w-[58px] largeDesktop:min-h-[58px] transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]">
                                </div>
                                <!-- Label: hidden at once on collapse; on expand it fades in once the sidebar has widened, on one line -->
                                <div class="flex items-center justify-between min-w-0 overflow-hidden whitespace-nowrap transition-opacity ease-out"
                                    :class="isCollapsed ? 'opacity-0 w-0 duration-100' : 'opacity-100 flex-1 duration-300 delay-200'">
                                    <span class="text-[16px] font-roboto font-medium text-black-100 truncate">
                                        {{ item.name }}
                                        <span v-if="item.comingSoon"
                                            class="text-xs  ml-1 font-roboto text-[#999999]">(Coming
                                            Soon)</span>
                                    </span>
                                    <div class="flex items-center gap-3">
                                        <span v-if="!item.noBadge && getPlanCount('bundle') > 0"
                                            class="inline-block text-sm min-w-10 text-center font-bold px-2 rounded-full text-blueLight-100 bg-gray-200 transition-all duration-100 ease-[cubic-bezier(0,0,0,0.5)]">
                                            {{ getPlanCount('bundle') }}
                                        </span>
                                        <div v-if="!isCollapsed && item.submenu"
                                            class="transition-all duration-100 ease-[cubic-bezier(0,0,0,0.5)] flex items-center justify-center w-[20px] tablet:w-[22px] h-[20px] tablet:h-[22px] transform mr-[10px]"
                                            :class="{ 'rotate-0': item.submenu && (!isCollapsed && activeMenu === item.name), 'rotate-180': !(item.submenu && (!isCollapsed && activeMenu === item.name)) }">
                                            <!-- <i
                                                class="fa fa-angle-up !text-[30px] font-bold text-gray-600 transition-all duration-500 ease-[cubic-bezier(0,0,0,0.5)]" /> -->
                                            <img src="../assets/images/arrow-down.svg" alt="arrow"
                                                class="transition-all duration-500 ease-[cubic-bezier(0,0,0,0.5)] w-full object-contain">
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </component>

                        <!-- Submenu - Added transition classes -->
                        <!-- open sidebar: inline submenu. Collapsed: same list as a flyout to the right of the icon -->
                        <ul v-if="item.submenu && (isCollapsed ? flyoutMenu === item.name : activeMenu === item.name)"
                            class="sbSubIn p-2 flex flex-col gap-1 origin-top"
                            :class="isCollapsed ? 'sbFlyout absolute left-full top-0 ml-[14px] z-[60] w-[260px] m-0 list-none bg-white rounded-2xl shadow-[0px_4px_20px_0px_#00000026] border border-[#E9ECF1]' : ''">
                            <!-- {{ item.submenu }} -->
                            <div v-for="sub in item.submenu" :key="sub.name">
                                <!-- hover / active colour by CSS :hover (sbSubItem in eval-figma.css), not JS flags that could stick -->
                                <li class="sbSubItem relative group/submenu text-md font-roboto pl-6 pr-1 py-1.5 cursor-pointer rounded-lg border border-[#CCCCCC]  text-black-100 transition-all duration-300"
                                    :class="{
                                        'active-route-class sbActive': isRouteActive(sub),
                                        'bg-[#A084E8]': sub.isOpen,
                                        '!bg-white': !sub.isOpen
                                    }" :style="{ '--sb-color': item.menuHoverColor || '#4b5563' }"
                                    @click.stop="sub.submenu ? null : handleSubmenuClick(sub, $event)">
                                    <!-- Submenu content (leaf: real <a href>; the li click covers its padding) -->
                                    <component :is="!sub.submenu && linkTo(sub.route) ? 'router-link' : 'div'"
                                        :to="(!sub.submenu && linkTo(sub.route)) || undefined" active-class=""
                                        exact-active-class=""
                                        class="flex justify-between items-center no-underline text-inherit transition-all duration-800">
                                        <div class="text-[14px] largeDesktop:text-[16px] rounded px-1.5 py-0.5 transition-all duration-300"
                                            :class="{
                                                'text-[#fff]': sub.isOpen,
                                            }">
                                            {{ sub.name }}
                                            <span v-if="sub.comingSoon"
                                                class="text-xs  ml-1 font-roboto text-[#999999] group-hover/submenu:text-white transition-all duration-300">(Coming
                                                Soon)</span>
                                        </div>
                                        <div v-if="sub.submenu"
                                            class="transition-all duration-800 flex items-center justify-center w-6 h-6"
                                            :class="{ 'rotate-180': sub.isOpen }" @click.stop="handleSubmenuClick(sub)">
                                            <i class="fa fa-angle-down !text-[24px] font-bold group-hover/submenu:!text-white transition-all duration-300"
                                                :class="isRouteActive(sub) || sub.isOpen ? 'text-white' : 'text-gray-600'" />
                                        </div>
                                        <div
                                            class="absolute left-2 h-full flex items-center transition-all duration-300">
                                            <span class="sbDot block w-2 h-2 rounded-full transition-all duration-300"
                                                :class="{
                                                    '!bg-[#fff]': sub.isOpen,
                                                }" />
                                        </div>
                                    </component>
                                </li>

                                <!-- Nested Submenu - Added transition classes -->
                                <div v-if="sub.submenu && sub.isOpen"
                                    class="py-3 transition-all duration-300 ease-in-out origin-top">
                                    <ul
                                        class="ml-4 mt-1 px-2 py-0 flex flex-col gap-1 relative rounded-lg transition-all duration-300">
                                        <div
                                            class="w-[3px] h-full absolute left-2 top-0 rounded-full bg-gray-300 transition-all duration-300" />
                                        <li v-for="nestedSub in sub.submenu" :key="nestedSub.name"
                                            class="sbNestedItem relative group-childMenu text-[14px] largeDesktop:text-[16px] font-roboto flex justify-between items-center pl-3 pr-3 py-1.5 cursor-pointer rounded-lg bg-white text-black-100 transition-all duration-300"
                                            :class="{ 'active-route-class sbActive': isRouteActive(nestedSub) }"
                                            :style="{ '--sb-color': item.menuHoverColor || '#4b5563' }"
                                            @click="handleSubmenuClick(nestedSub, $event)">
                                            <component
                                                :is="!nestedSub.submenu && linkTo(nestedSub.route) ? 'router-link' : 'div'"
                                                :to="(!nestedSub.submenu && linkTo(nestedSub.route)) || undefined"
                                                active-class="" exact-active-class=""
                                                class="flex flex-1 justify-between items-center no-underline text-inherit">
                                                {{ nestedSub.name }}
                                                <span v-if="nestedSub.label && getPlanCount(nestedSub.label) > 0"
                                                    class="inline-block text-sm min-w-10 text-center font-bold px-2 rounded-full text-[#A084E8] bg-gray-200 transition-all duration-100 ease-[cubic-bezier(0,0,0,0.5)]">
                                                    {{ getPlanCount(nestedSub.label) }}
                                                </span>
                                                <div
                                                    class="absolute top-0 left-0 h-full flex items-center transition-all duration-300">
                                                    <span
                                                        class="sbBar block w-[3px] h-full rounded-full transition-all duration-300" />
                                                </div>
                                            </component>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </ul>
                    </li>
                </ul>
            </nav>
        </div>
    </div>
</template>

<script>
import { markMenuNav } from "@/helpers/crumbTrail";
import navShowIcon from "../assets/images/nav-show-icon.svg"
import navHideIcon from "../assets/images/nav-hide-icon.svg"
import { mapActions, mapGetters } from "vuex";
import { isNewTabClick } from "@/helpers/newTabClick";
import { loadOnce } from "@/helpers/loadOnce";
import { withTechMenu, menuRouteFor } from "@/helpers/techMenu";

export default {
    props: ['setSideMenuClicked'],
    data() {
        return {
            user: JSON.parse(localStorage.getItem('user')) ?? null,
            isCollapsed: false,
            // collapsed sidebar: which submenu is shown as a flyout
            flyoutMenu: '',
            activeMenu: '',
            showIcon: navShowIcon,
            hideIcon: navHideIcon,
            isHovered: false,
            cartCount: {},
            menuItems: [],
            menuDataLoaded: false
        };
    },
    computed: {
        ...mapGetters(["sideMenuData", "cartCountResponse", "userObject"]),
        currentRoute() {
            return menuRouteFor(this.$route);
        },
    },
    watch: {
        '$route.path'(newPath) {
            console.log('Route changed to:', newPath);
            this.$nextTick(() => {
                if (this.menuDataLoaded) {
                    this.activateMenuByRoute();
                }
            });
        },

        sideMenuData: {
            handler: function (data) {
                if (Array.isArray(data)) {
                    console.log('Side menu data loaded');
                    this.menuItems = withTechMenu(JSON.parse(JSON.stringify(data)));
                    this.menuDataLoaded = true;

                    this.$nextTick(() => {
                        this.initSubmenus();
                        this.activateMenuByRoute();
                    });
                }
            },
            immediate: true,
            deep: true
        },

        cartCountResponse: function (data) {
            this.cartCount = this.cartCountResponse;
        },

        userObject: function () {
            localStorage.setItem('user', JSON.stringify(this.userObject));
        },
    },
    created() {
        this.initSubmenus();
    },
    beforeUnmount() {
        document.removeEventListener('mousedown', this.closeFlyoutOutside);
    },
    mounted() {
        document.addEventListener('mousedown', this.closeFlyoutOutside);
        loadOnce('profile', () => this.getProfile({}));
        this.setMenu();

        // Restore only sidebar collapse state from localStorage — desktop sidebar only: the header's mobile menu
        // (it passes setSideMenuClicked) always shows the full menu, else a desktop "collapsed" left it icon-only
        const savedSidebarState = localStorage.getItem('sidebarCollapsed');
        if (savedSidebarState !== null && !this.setSideMenuClicked) {
            this.isCollapsed = savedSidebarState === 'true';
        }

        this.$nextTick(() => {
            console.log('Component mounted, waiting for menu data...');

            const is_from_mobile = localStorage.getItem("is_from_mobile", 0) ?? 0;
            if ((localStorage.getItem('user') ?? null) != null && is_from_mobile != '1') {
                if (this.$helper.isEmpty(this.$store.state.product.cartCountResponse)) {
                    loadOnce('cartCount', () => this.getCartCount({}));
                } else {
                    this.cartCount = this.$store.state.product.cartCountResponse;
                }
            }
        });
    },
    methods: {
        markMenuNav,
        ...mapActions(["getSideMenu", "getCartCount", "setSelectedProtectionPlan", "resetSate", "getProfile"]),

        getPlanCount(plan) {
            return this.cartCount[plan] ?? null;
        },

        // click outside the sidebar closes the collapsed flyout
        closeFlyoutOutside(e) {
            if (this.flyoutMenu && this.$el && !this.$el.contains(e.target)) this.flyoutMenu = '';
        },

        toggleSidebar() {
            this.isCollapsed = !this.isCollapsed;
            this.activeMenu = '';
            this.flyoutMenu = '';
            // Save only sidebar collapse state
            localStorage.setItem('sidebarCollapsed', this.isCollapsed);
            this.$emit('sidebar-collapse', this.isCollapsed);
        },

        // in-app path → rendered as <router-link> (new tab works); '#', external or empty routes stay click-only
        linkTo(route) {
            return typeof route === 'string' && route.startsWith('/') && !route.startsWith('//') ? route : null;
        },

        handleMenuClick(item, e) {
            if (this.linkTo(item.route) && isNewTabClick(e)) return;
            if (item.name == 'Home Essentials Bundles' && item.route != this.$route.path) {
                this.resetSate();
            }

            if (item.route && !item.submenu) {
                // router-link already navigates on a plain click
                if (!this.linkTo(item.route)) this.$router.push(item.route);
                // this.$router.push(item.route);
                // this.setSelectedProtectionPlan(item);
                return;
            }

            // Collapsed: open / close the submenu as a flyout
            if (this.isCollapsed && item.submenu) {
                this.flyoutMenu = this.flyoutMenu === item.name ? '' : item.name;
                return;
            }

            // Toggle the active menu - NO localStorage usage
            if (this.activeMenu === item.name) {
                this.activeMenu = '';
            } else {
                this.activeMenu = item.name;
            }
        },

        handleSubmenuClick(subItem, e) {
            if (subItem.submenu) {
                // Toggle nested submenu - NO localStorage usage
                subItem.isOpen = !subItem.isOpen;
            } else if (subItem.route) {
                const viaLink = e && e.target && e.target.closest && e.target.closest('a');
                if (viaLink && isNewTabClick(e)) return;
                if (this.setSideMenuClicked) {
                    this.setSideMenuClicked();
                }
                // a click on the <a> is navigated by router-link; padding around it lands here
                if (!viaLink) this.$router.push(subItem.route);
                // this.setSelectedProtectionPlan(subItem);

                if (this.isCollapsed) {
                    this.activeMenu = '';
                    this.flyoutMenu = '';
                }
            }
        },

        // Fixed: Now sets activeMenu to the TOP-LEVEL parent (Partner) instead of immediate parent (NRG)
        activateMenuByRoute() {
            if (!this.menuDataLoaded || this.menuItems.length === 0) {
                return;
            }
            // partner checkout default omega plan select
            const currentRoute = (this.$route.name == 'globalSearch') ? '/protection-plan/nrg/bundle-list' : menuRouteFor(this.$route);
            console.log('Activating menu for route:', currentRoute, this.$route.path);

            // Reset all menu states first
            this.resetAllMenuStates();

            let foundRoute = false;
            let matchedItem = null; // Store the matched menu item

            // Find the menu item that matches the current route and open ALL parent menus
            const findAndActivateRoute = (items, parentChain = []) => {
                for (const item of items) {
                    // Check if this item matches the current route
                    if (item.route === currentRoute) {
                        console.log('Found matching route for item:', item.name, item.route);
                        matchedItem = item; // Store the matched item

                        // top-level item (e.g. Dashboard): close any section left open by the previous page
                        if (parentChain.length === 0) {
                            this.activeMenu = '';
                        }
                        if (parentChain.length > 0) {
                            // Set activeMenu to the TOP-LEVEL parent (first in chain)
                            const topLevelParent = parentChain[0];
                            this.activeMenu = topLevelParent.name;
                            console.log('Setting active menu to TOP-LEVEL parent:', this.activeMenu);

                            // Open ALL parent menus in the chain (both Partner and NRG)
                            parentChain.forEach(parent => {
                                if (parent.submenu) {
                                    parent.isOpen = true;
                                    console.log('Opening parent menu:', parent.name);
                                }
                            });
                        }

                        foundRoute = true;
                        return true;
                    }

                    // Recursively check submenus
                    if (item.submenu) {
                        const found = findAndActivateRoute(item.submenu, [...parentChain, item]);
                        if (found) {
                            foundRoute = true;
                            return true;
                        }
                    }
                }
                return false;
            };

            // Try to find and activate based on current route
            findAndActivateRoute(this.menuItems);

            // Call setSelectedProtectionPlan if a route was matched
            if (foundRoute && matchedItem) {
                console.log('Calling setSelectedProtectionPlan for matched item:', matchedItem.name);
                this.setSelectedProtectionPlan(matchedItem);
            }

            if (!foundRoute) {
                console.log('No matching route found, resetting active menu');
                this.activeMenu = '';
            }

            console.log('Final active menu:', this.activeMenu);
        },

        // Helper to reset all menu open states
        resetAllMenuStates() {
            const resetItems = (items) => {
                items.forEach(item => {
                    if (item.submenu) {
                        item.isOpen = false;
                        resetItems(item.submenu);
                    }
                });
            };
            resetItems(this.menuItems);
        },

        initSubmenus() {
            const walk = items => {
                items.forEach(item => {
                    item._hover = false;
                    if ('isOpen' in item) item.isOpen = false;
                    if (item.submenu) walk(item.submenu);
                });
            };
            walk(this.menuItems);
        },

        isRouteActive(item) {
            if (item.route) return this.currentRoute === item.route;
            if (item.submenu) return item.submenu.some(sub => this.isRouteActive(sub));
            return false;
        },

        getMenuItemClasses(item) {
            const isActive = this.isRouteActive(item);
            return [
                // collapsed: keep the 1px border but transparent, so it fades with the width instead of popping
                this.isCollapsed ? '!border-transparent !bg-transparent' : ' bg-white mobile:shadow-[0_0px_15px_-5px_#b5b2b2]',
                isActive ? 'active-route-class' : '',
            ];
        },

        getMenuItemStyles(item) {
            const isActive = this.isRouteActive(item);

            if (isActive) {
                return {
                    'border-color': item.menuHoverColor || '#A084E8',
                    'border-style': 'solid',
                    'border-radius': '1rem'
                };
            }

            // Submenu + active menu logic
            if (item.submenu && (!this.isCollapsed && this.activeMenu === item.name)) {
                return {
                    'border-color': item.menuHoverColor || '#A084E8',
                    'border-style': 'solid',
                    'border-radius': '1rem',
                };
            }

            return {};
        },

        setMenu() {
            loadOnce('sideMenu', () => this.getSideMenu({}));
        },

        // Old menu setup methods (keep for reference)
        addMenu(item) {
            this.menuItems.push(item);
        },

        addSubMenu(parentName, item) {
            const parent = this.findMenuByName(parentName, this.menuItems);
            if (parent) {
                parent.submenu.push(item);
            }
        },

        removeSubMenu(parentName, subMenuName) {
            const parent = this.findMenuByName(parentName, this.menuItems);
            if (parent && parent.submenu) {
                parent.submenu = parent.submenu.filter(sub => sub.name !== subMenuName);
            }
        },

        findMenuByName(name, items) {
            for (let item of items) {
                if (item.name === name) return item;
                if (item.submenu && item.submenu.length) {
                    let found = this.findMenuByName(name, item.submenu);
                    if (found) return found;
                }
            }
            return null;
        },
    }
};
</script>
<style>
/* submenu opens with a short fade + slide instead of popping in */
.sbSubIn {
    animation: sbSubIn 0.25s ease-out;
}

@keyframes sbSubIn {
    from {
        opacity: 0;
        transform: translateY(-4px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
